import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { RootServicesModule } from './root-services.module';
import { of, Observable, BehaviorSubject } from 'rxjs';
import { IAuthResponse } from '@app-root-interfaces';
import { tap, catchError } from 'rxjs/operators';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { AccountInfo, InteractionStatus, EventMessage, EventType } from '@azure/msal-browser';
import { TokenClaims } from '@scf-monorepo/interfaces';
import { GraphService } from './graph.service';

@Injectable({
    providedIn: RootServicesModule
})
export class AuthService {
    private msalService = inject(MsalService);
    private msalBroadcastService = inject(MsalBroadcastService);
    private graphService = inject(GraphService);

    private _isAuthenticated: WritableSignal<boolean> = signal<boolean>(false);
    private _isAuthLoading: WritableSignal<boolean> = signal<boolean>(true);
    private _userAccount: WritableSignal<AccountInfo | null> = signal<AccountInfo | null>(null);
    private _userPhoto: WritableSignal<Blob | null> = signal<Blob | null>(null);

    public isAuthenticated$: Observable<boolean>;
    public authResponse$: BehaviorSubject<IAuthResponse | null> = new BehaviorSubject<IAuthResponse | null>(null);
    public userIdTokenClaims: Signal<TokenClaims | null> = computed(
        () => this._userAccount()?.idTokenClaims as TokenClaims
    );

    constructor() {
        this.initializeAuth();
    }

    private initializeAuth(): void {
        this.subscribeToLoginAndLogout();
        this.subscribeToAuthenticationDone();
    }

    private subscribeToAuthenticationDone() {
        this.msalBroadcastService.inProgress$
            .pipe(
                tap((status: InteractionStatus) => {
                    this._isAuthLoading.set(status !== InteractionStatus.None);

                    if (status === InteractionStatus.None) {
                        this.updateAuthState();
                    }
                })
            )
            .subscribe();
    }

    private subscribeToLoginAndLogout() {
        this.msalBroadcastService.msalSubject$
            .pipe(
                tap((msg: EventMessage) => {
                    if (msg.eventType === EventType.LOGOUT_SUCCESS) {
                        this._isAuthenticated.set(false);
                    }
                })
            )
            .subscribe();
    }

    private updateAuthState(): void {
        const accounts = this.msalService.instance.getAllAccounts();

        if (accounts.length > 0) {
            const activeAccount = this.msalService.instance.getActiveAccount() || accounts[0];
            if (activeAccount && activeAccount.idTokenClaims) {
                this.msalService.instance.setActiveAccount(activeAccount);
                this._userAccount.set(activeAccount);
                this._isAuthenticated.set(true);
                this.loadUserPhoto();
            } else {
                this.setUnauthorizedUserSignals();
            }
        } else {
            this.setUnauthorizedUserSignals();
        }

        this._isAuthLoading.set(false);
    }

    private setUnauthorizedUserSignals() {
        this._isAuthenticated.set(false);
        this._userAccount.set(null);
        this._userPhoto.set(null);
    }

    public getUserPhotoUrl(): string | undefined {
        const photo = this._userPhoto();
        return photo ? URL.createObjectURL(photo) : undefined;
    }

    private loadUserPhoto(): void {
        const oid = this.userIdTokenClaims()?.oid;
        this.graphService
            .getPhotoForUser(oid ?? 'me')
            .pipe(
                tap((photo) => this._userPhoto.set(photo)),
                catchError((error) => {
                    console.error('Error loading user photo:', error);
                    return of(null);
                })
            )
            .subscribe();
    }

    public getUserData(): Observable<TokenClaims> {
        return of(this.userIdTokenClaims());
    }

    public getLoginData(): Observable<IAuthResponse> {
        return this.authResponse$;
    }
}
